import { useState, useMemo, useEffect, useRef } from 'react';

import Text from '@ui/text/text/text';
import Container from '@ui/container/container';
import Button from '@ui/buttons/button/button';
import Icon from '@ui/icons/icon';
import { ICONS } from '@ui/icons/types';
import RotatableIcon from '@ui/RotatableIcon/rotatable-icon';
import Popover from '@ui/popover/popover';
import EditStateWrapper from '@ui/edit-state-wrapper/edit-state-wrapper';
import InputWithOverlay from '@ui/input-with-overlay/input-with-overlay';
import { getUserTimezone } from '@ui/calendar/utils';
import type { PopoverPositionOptions } from '@ui/popover/popover.types';

type TimezoneSelectProps = {
  selectedTimezone?: string;
  onChange?: (timezone: string) => void;
  hideLabel?: boolean;
  forcePosition?: PopoverPositionOptions;
};

const allOptions = [
  { value: 'Etc/GMT+12', label: '(GMT-12:00) International Date Line West' },
  { value: 'Pacific/Pago_Pago', label: '(GMT-11:00) Midway Island, Samoa' },
  { value: 'Pacific/Honolulu', label: '(GMT-10:00) Hawaii' },
  { value: 'America/Anchorage', label: '(GMT-09:00) Alaska' },
  { value: 'America/Los_Angeles', label: '(GMT-08:00) Pacific Time' },
  { value: 'America/Denver', label: '(GMT-07:00) Mountain Time' },
  { value: 'America/Phoenix', label: '(GMT-07:00) Arizona' },
  { value: 'America/Chicago', label: '(GMT-06:00) Central Time' },
  { value: 'America/New_York', label: '(GMT-05:00) Eastern Time' },
  { value: 'America/Halifax', label: '(GMT-04:00) Atlantic Time' },
  { value: 'America/St_Johns', label: '(GMT-03:30) Newfoundland' },
  { value: 'America/Sao_Paulo', label: '(GMT-03:00) Brazil, Buenos Aires' },
  { value: 'Atlantic/South_Georgia', label: '(GMT-02:00) Mid-Atlantic' },
  { value: 'Atlantic/Azores', label: '(GMT-01:00) Azores' },
  { value: 'Europe/London', label: '(GMT+00:00) London, Dublin, Lisbon' },
  { value: 'Europe/Berlin', label: '(GMT+01:00) Berlin, Paris, Rome, Madrid' },
  { value: 'Europe/Athens', label: '(GMT+02:00) Athens, Cairo, Jerusalem' },
  { value: 'Europe/Moscow', label: '(GMT+03:00) Moscow, Riyadh' },
  { value: 'Asia/Tehran', label: '(GMT+03:30) Tehran' },
  { value: 'Asia/Dubai', label: '(GMT+04:00) Dubai, Baku' },
  { value: 'Asia/Kabul', label: '(GMT+04:30) Kabul' },
  { value: 'Asia/Karachi', label: '(GMT+05:00) Islamabad, Karachi' },
  { value: 'Asia/Kolkata', label: '(GMT+05:30) India' },
  { value: 'Asia/Kathmandu', label: '(GMT+05:45) Kathmandu' },
  { value: 'Asia/Dhaka', label: '(GMT+06:00) Dhaka' },
  { value: 'Asia/Yangon', label: '(GMT+06:30) Yangon' },
  { value: 'Asia/Bangkok', label: '(GMT+07:00) Bangkok, Jakarta' },
  { value: 'Asia/Shanghai', label: '(GMT+08:00) Beijing, Singapore, Perth' },
  { value: 'Asia/Tokyo', label: '(GMT+09:00) Tokyo, Seoul' },
  { value: 'Australia/Adelaide', label: '(GMT+09:30) Adelaide' },
  { value: 'Australia/Sydney', label: '(GMT+10:00) Sydney, Melbourne' },
  { value: 'Pacific/Guadalcanal', label: '(GMT+11:00) Solomon Islands' },
  { value: 'Pacific/Auckland', label: '(GMT+12:00) Auckland, Wellington' },
  { value: 'Pacific/Apia', label: '(GMT+13:00) Samoa' },
  { value: 'Pacific/Kiritimati', label: '(GMT+14:00) Line Islands' },
];

const TimezoneSelect = ({
  selectedTimezone,
  onChange,
  hideLabel,
  forcePosition = 'bottom-left',
}: TimezoneSelectProps) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const tz = useMemo(() => {
    return selectedTimezone || getUserTimezone() || 'Europe/London';
  }, [selectedTimezone]);

  const activeOption = useMemo(() => {
    if (!selectedTimezone) return null;
    return (
      allOptions.find((o) => o.value === tz) ?? allOptions.find((o) => o.value === 'Europe/London') ?? allOptions[0]
    );
  }, [selectedTimezone, tz]);

  const tzLabel = activeOption?.label.replace(/^\(GMT[^)]*\)\s*/, '') ?? '';
  const tzOffset = activeOption?.label.match(/^\(GMT[^)]*\)/)?.[0] ?? '';

  const filtered = useMemo(() => {
    const query = search.toLowerCase().trim();
    if (!query) return allOptions;
    return allOptions.filter((o) => o.label.toLowerCase().includes(query));
  }, [search]);

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        const activeEl = scrollContainerRef.current?.querySelector('[data-active="true"]');
        activeEl?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }, 100);
    }
  }, [open]);

  return (
    <EditStateWrapper
      Label={hideLabel ? undefined : 'TIMEZONE'}
      className="position-relative"
      headerClassName="position-absolute width-100-percent"
      applyTabSentinels
    >
      {({ isFocusedWithin }) => (
        <div className="margin-t-24" data-show-edit-label={isFocusedWithin ? 'false' : 'true'}>
          <Popover
            closeOnClick={false}
            forcePosition={forcePosition}
            open={open}
            onClose={() => {
              setOpen(false);
              setSearch('');
            }}
            border="none"
            verticalOffset={8}
            width="fit-content"
            cycleWithArrowKeys
            containerClassName="z-11"
            renderTriggerButton={() => (
              <Button
                className="clear-button"
                onClick={() => setOpen((prev) => !prev)}
                onKeyDown={(e) => {
                  if (['ArrowUp', 'ArrowDown'].includes(e.key)) {
                    e.preventDefault();
                    setOpen(true);
                  }
                }}
              >
                <Container
                  padding="12px"
                  className={`S3 text-left position-relative ${
                    !activeOption
                      ? 'flex-column jc-space-around'
                      : !isFocusedWithin
                        ? 'flex ai-center gap-16'
                        : 'flex-column jc-center gap-4'
                  }`}
                  borderRadius="8px"
                  width="200px"
                  height="76px"
                  background={
                    open
                      ? 'var(--global-cont-2)'
                      : activeOption && !isFocusedWithin
                        ? 'transparent'
                        : 'var(--global-cont-1)'
                  }
                  hoverBackground={
                    open ? 'var(--global-cont-2)' : activeOption ? 'transparent' : 'var(--global-cont-2)'
                  }
                >
                  <Icon
                    name={ICONS.TIMEZONE}
                    size={activeOption && isFocusedWithin ? 16 : 20}
                    color="var(--global-ico-text-3)"
                  />

                  <div className="flex-column overflow-hidden">
                    {activeOption ? (
                      <>
                        <Text className="S3" design="GlobalIcoText1" clamp={1}>
                          {tzLabel}
                        </Text>
                        <Text className="C1 margin-t-2" design="GlobalIcoText2">
                          {tzOffset}
                        </Text>
                      </>
                    ) : (
                      <Text className="C1" design="GlobalIcoText3">
                        Set timezone
                      </Text>
                    )}
                  </div>

                  {(isFocusedWithin || !activeOption) && (
                    <Container className={`position-absolute ${!activeOption ? 'bottom-14' : 'bottom-10'} right-12`}>
                      <RotatableIcon
                        name={ICONS.SELECT_DROPDOWN_CARET}
                        size={16}
                        color="var(--global-ico-text-3)"
                        rotate={open ? '180deg' : '0deg'}
                      />
                    </Container>
                  )}
                </Container>
              </Button>
            )}
          >
            {() => (
              <Container design="GlobalCont2" borderRadius="12px" className="overflow-hidden" width="300px">
                <div className="padding-8">
                  <InputWithOverlay
                    isBorderless
                    inputHeight="36px"
                    background="var(--global-cont-3)"
                    inputProps={{
                      autoFocus: true,
                      value: search,
                      onChange: (e) => setSearch(e.target.value),
                      placeholder: 'Search city or offset...',
                    }}
                  />
                </div>

                <Container
                  ref={scrollContainerRef}
                  maxHeight="240px"
                  className="no-scrollbar overflow-y-scroll padding-b-8"
                >
                  {filtered.map((opt) => {
                    const isActive = !!selectedTimezone && opt.value === tz;
                    return (
                      <Button
                        key={opt.value}
                        data-active={isActive}
                        className="clear-button width-100-percent text-left S3 padding-v-10 padding-h-16"
                        backgroundColor={isActive ? 'var(--global-cont-3)' : 'transparent'}
                        hoverBackgroundColor="var(--global-cont-3)"
                        onClick={() => {
                          onChange?.(opt.value);
                          setOpen(false);
                          setSearch('');
                        }}
                      >
                        <Text className="S3" design={isActive ? 'GlobalIcoText1' : 'GlobalIcoText2'}>
                          {opt.label}
                        </Text>
                      </Button>
                    );
                  })}
                </Container>
              </Container>
            )}
          </Popover>
        </div>
      )}
    </EditStateWrapper>
  );
};

export default TimezoneSelect;
